// v3 windmill, authored the way age-of-mcp writes it: local field helpers + the
// shared styleV3 part makers, instead of a flat wall of literals. A tapered
// limewashed tower under a wheat-gold (ROOF_FOOD) cone cap whose icon IS the
// four big canvas sails: each sail bar's long axis lies ALONG its radius
// (rotated ang − π/2, never tangential), and the whole sail disc is RAKED back
// toward the camera so the X of sails reads clearly from above. A millstone and
// grain sacks dress the open yard. Ported 1:1 from age-of-mcp's v3/windmill.ts.
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, poles, slab, CANVAS, PLASTER, ROOF_FOOD, STONE, TIMBER } = styleV3;

const BASE = 0.12;            // foundation slab top
const RAKE = -0.35;           // tilt of the sail disc — face raised to the camera

// The tapered round mill tower, `h` tall on the foundation.
function tower(h: number): Part {
  return cyl(0.24, 0.34, h, 10, PLASTER, [0, BASE + h / 2, 0]);
}

// The wheat-gold cone cap on top — the food-chain roof code.
function cap(topY: number): Part {
  return cyl(0.02, 0.3, 0.24, 10, ROOF_FOOD, [0, topY + 0.12, 0]);
}

// The mill door on the front face.
function door(): Part {
  return box(0.2, 0.34, 0.05, TIMBER, [0, BASE + 0.17, 0.33]);
}

// Four big canvas sails radiating from a tilted hub on the front face. Each
// bar runs ALONG its radius (rotation ang − π/2 about z, applied first), and
// the whole disc is then raked about x so it faces up toward the camera.
function sails(hubY: number): Part[] {
  const hubZ = 0.31;
  const cosR = Math.cos(RAKE), sinR = Math.sin(RAKE);
  const out: Part[] = [
    cyl(0.05, 0.05, 0.1, 6, TIMBER, [0, hubY, hubZ], [Math.PI / 2 + RAKE, 0, 0]),
  ];
  for (let i = 0; i < 4; i++) {
    const ang = i * Math.PI / 2 + Math.PI / 4;
    const r = 0.27;             // sail centre, half-way out along the radius
    out.push(box(0.12, 0.54, 0.02, CANVAS, [
      Math.cos(ang) * r,
      hubY + Math.sin(ang) * r * cosR,
      hubZ + 0.04 + Math.sin(ang) * r * sinR,
    ], [RAKE, 0, ang - Math.PI / 2]));
  }
  return out;
}

// A spare millstone and grain sacks dressing the open yard.
function yard(): Part[] {
  return [
    cyl(0.13, 0.13, 0.05, 10, STONE, [0.33, BASE + 0.025, 0.3]),
    box(0.15, 0.17, 0.15, CANVAS, [-0.32, BASE + 0.085, 0.32], [0, 0.3, 0]),
    box(0.12, 0.13, 0.12, CANVAS, [-0.2, BASE + 0.065, 0.38], [0, -0.2, 0]),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.9, 0.9)];
  if (s === 1) return [slab(0.9, 0.9), tower(0.4), ...poles(0.74, 0.74, 0.7)];
  if (s === 2) return [slab(0.9, 0.9), tower(0.95), door(), ...poles(0.74, 0.74, 1.15)];
  const h = 0.95, topY = BASE + h;
  return [
    slab(0.9, 0.9), tower(h), cap(topY), door(),
    ...sails(topY - 0.12), ...yard(),
  ];
}

export const windmillV3 = defineModel({
  id: "windmill-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Windmill (v3)",
  tags: ["building","windmill","low-poly","age-of-mcp","v3"],
  summary: "Low-poly windmill.",
  description: "A sail-driven mill that grinds grain into flour.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench", position: [0, 0.12, 0.44], facing: Math.PI },
  ],
  interactions: [
    { id: "work", label: "Working the mill", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
